import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import './onboarding/ToneArchetypeStep.css';

function ToneArchetypeSettings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';

  const [archetypes, setArchetypes] = useState([]);
  const [initialId, setInitialId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [listRes, userRes] = await Promise.all([
          apiClient.get('/api/v1/tone-archetypes'),
          apiClient.get('/api/v1/users/me/tone-archetype'),
        ]);
        if (cancelled) return;
        setArchetypes(listRes.data || []);
        const currentId = userRes.data?.tone_archetype || null;
        setInitialId(currentId);
        setSelectedId(currentId);
      } catch (err) {
        if (!cancelled) setError(t('profilePage.toneArchetype.error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleSelect = (id) => {
    setSelectedId(id);
    if (error) setError(null);
    if (success) setSuccess(false);
  };

  const handleSave = async () => {
    if (!selectedId || selectedId === initialId) return;
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(false);
      await apiClient.put('/api/v1/users/me/tone-archetype', {
        archetype_id: selectedId,
      });
      setInitialId(selectedId);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || t('profilePage.toneArchetype.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="tone-step">
        <div className="tone-loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const canSave = !!selectedId && selectedId !== initialId && !submitting;

  return (
    <div className="tone-step">
      {error && <div className="tone-step-error">{error}</div>}
      {success && (
        <div className="tone-step-error" style={{ background: '#e6f7ec', color: '#1a7f37', borderColor: '#1a7f37' }}>
          {t('profilePage.toneArchetype.saved')}
        </div>
      )}

      <div className="archetype-grid">
        {archetypes.map((archetype) => (
          <button
            key={archetype.id}
            type="button"
            className={`archetype-card ${selectedId === archetype.id ? 'selected' : ''}`}
            onClick={() => handleSelect(archetype.id)}
            disabled={submitting}
          >
            <span className="archetype-icon">{archetype.icon}</span>
            <span className="archetype-label">{archetype[`label_${lang}`]}</span>
            <span className="archetype-tagline">{archetype[`tagline_${lang}`]}</span>
            <span className="archetype-description">{archetype[`short_description_${lang}`]}</span>
          </button>
        ))}
      </div>

      <p className="tone-step-notice">{t('profilePage.toneArchetype.notice')}</p>

      <button
        type="button"
        className="btn btn-primary btn-large"
        onClick={handleSave}
        disabled={!canSave}
      >
        {submitting
          ? t('profilePage.toneArchetype.saving')
          : t('profilePage.toneArchetype.save')}
      </button>
    </div>
  );
}

export default ToneArchetypeSettings;
